import React from 'react'
import styled from 'styled-components'

// Components
import Text from './Text'
import Icon from './icon/Icon'
import AmountTag from './tag/AmountTag'

interface IProps {
  caption: string
  /** Which way the chevron points. Up once the thing it controls is open. */
  open?: boolean
  amount?: number
  showNotification?: boolean
  disabled?: boolean
  /** Raw colours, not theme keys: a card in a company colour tints its own. */
  color?: string
  hoverColor?: string
  onClick?: () => void
  className?: string
}

/**
 * The quietest button we have: a small-caps label with a chevron beside it,
 * faded until the cursor arrives, optionally carrying a count or an unread dot.
 * The chevron keeps to the far end whenever the button is given a width, so a
 * stack of them lines up down a card.
 *
 * Fold wears one above its content; a page that commands a fold from the
 * outside — expand all, collapse all — uses the same button on its own.
 */
const LabelButton = ({
  caption,
  open,
  amount,
  showNotification,
  disabled,
  color,
  hoverColor,
  onClick,
  className,
}: IProps) => (
  <Container
    type="button"
    className={className}
    disabled={disabled}
    color={color}
    hoverColor={hoverColor}
    onClick={onClick}
  >
    {showNotification && <Notification />}
    <Caption tag="span" textStyle="labelSmallCaps">
      {caption}
    </Caption>
    {!!amount && (
      <AmountTag
        size="small"
        amount={amount}
        color="textLightest"
        textColor="text"
      />
    )}
    <StyledIcon type={open ? 'ChevronUp' : 'ChevronDown'} size={12} />
  </Container>
)

export default LabelButton

const Caption = styled(Text)`
  margin-right: 8px;
`

/* Away from the label, hard against the other end: given a width the button
   spans a card, and the chevron marks where that card ends. Without one it
   collapses back against the label and the button hugs its own content. */
const StyledIcon = styled(Icon)`
  margin-left: auto;
`

const Notification = styled.span`
  width: 8px;
  height: 8px;
  flex: none;
  margin-right: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.secondary};
`

/* The label and the chevron move as one: both are faded at rest and both go
   primary under the cursor, so the pair reads as a single control rather than
   a word with a decoration next to it. */
const Container = styled.button<{ color?: string; hoverColor?: string }>`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border: 0;
  background: none;
  text-align: left;
  cursor: pointer;

  ${Caption} {
    color: ${({ theme, color }) => color ?? theme.colors.text72};
  }

  svg > path {
    fill: ${({ theme, color }) => color ?? theme.colors.text72};
  }

  &:hover {
    ${Caption} {
      color: ${({ theme, hoverColor }) => hoverColor ?? theme.colors.primary};
    }

    svg > path {
      fill: ${({ theme, hoverColor }) => hoverColor ?? theme.colors.primary};
    }
  }

  /* A spent command greys back rather than darkening: a solid dark would leave
     the button you cannot press heavier than the one you can. */
  &:disabled {
    cursor: default;

    ${Caption} {
      color: ${({ theme }) => theme.colors.textLighter};
    }

    svg > path {
      fill: ${({ theme }) => theme.colors.textLighter};
    }
  }
`
